import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from './root-store';
import type { AuthStore } from './auth-store';
import { AlertStore } from './alert-store';
import { LookupKeyEnum } from '../../defs/api';
import { routes } from '../../config/routes';

export class AuthDialogStore {
    rootStore: RootStore;

    authStore: AuthStore;

    alertStore: AlertStore;

    activeModal:
        | 'login'
        | 'forgottenPassword'
        | 'signUp'
        | 'partnerSignUp'
        | 'emailConfirmation'
        | 'resetPassword'
        | 'alreadyLoggedIn'
        | null = null;

    loading = false;

    confirmLoading = false;

    confirmDone = false;

    agreedTerms = false;

    agreedMarketing = false;

    showSignupPromo = false;

    constructor(authStore: AuthStore) {
        this.rootStore = authStore.rootStore;
        this.authStore = authStore;

        makeAutoObservable(this, {
            rootStore: false,
            authStore: false,
        });
    }

    closeDialog(): void {
        this.activeModal = null;
        this.showSignupPromo = false;
        this.rootStore.alertStore.clearAllMessages();
        this.rootStore.alertStore.setDefaultPlacement();
    }

    setShowSignupPromo(show: boolean): void {
        this.showSignupPromo = show;
    }

    setLoginActive(keepMessages = false): void {
        runInAction(() => {
            this.activeModal = 'login';
            this.showSignupPromo = false;
        });
        this.rootStore.alertStore.setModalPlacement();
        if (!keepMessages) {
            this.rootStore.alertStore.clearAllMessages();
        }
    }

    setForgottenPassActive(): void {
        this.activeModal = 'forgottenPassword';
        this.showSignupPromo = false;
        this.rootStore.alertStore.setModalPlacement();
        this.rootStore.alertStore.clearAllMessages();
    }

    setPartnerSignupActive(): void {
        runInAction(() => {
            this.activeModal = 'partnerSignUp';
        });
        this.rootStore.cookieService.setInterestedInSubscription(LookupKeyEnum.PremiumYearlyCzk202411);
        this.rootStore.alertStore.setModalPlacement();
        this.rootStore.alertStore.clearAllMessages();
    }

    setSignupActive(lookupKey?: LookupKeyEnum | null): void {
        const { cookieService } = this.rootStore;
        const affiliateEntry = cookieService.getRedirectUrl() === routes.app.affiliate;
        // An explicit Premium choice supersedes an earlier affiliate entry.
        if (affiliateEntry && lookupKey !== undefined) {
            cookieService.clearRedirectUrl();
        }
        cookieService.setInterestedInSubscription(
            affiliateEntry && lookupKey === undefined ? null : cookieService.getSignupLookupKey(lookupKey),
        );
        runInAction(() => {
            this.activeModal = 'signUp';
            this.showSignupPromo = this.rootStore.cookieService.hasPredepissimoneryPromo();
        });
        this.rootStore.alertStore.setModalPlacement();
        this.rootStore.alertStore.clearAllMessages();
    }

    setEmailConfirmationActive(): void {
        this.activeModal = 'emailConfirmation';
        this.showSignupPromo = false;
        this.rootStore.alertStore.setModalPlacement();
        this.rootStore.alertStore.clearAllMessages();
    }

    setResetPasswordActive(): void {
        this.activeModal = 'resetPassword';
        this.showSignupPromo = false;
        this.rootStore.alertStore.setModalPlacement();
        this.rootStore.alertStore.clearAllMessages();
    }

    setAlreadyLoggedInActive(): void {
        this.activeModal = 'alreadyLoggedIn';
        this.showSignupPromo = false;
        this.rootStore.alertStore.setModalPlacement();
        this.rootStore.alertStore.clearAllMessages();
    }

    setLoading(loading: boolean) {
        this.loading = loading;
    }

    switchAgreedTerms(): void {
        this.agreedTerms = !this.agreedTerms;
    }

    switchAgreedMarketing(): void {
        this.agreedMarketing = !this.agreedMarketing;
    }
}
